import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircleHelp, ExternalLink, Info, Search } from 'lucide-react';
import { useEditor } from '../../context/EditorContext';
import { helpFor } from '../legal/helpContent';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

/**
 * The ribbon's "?" — context-sensitive help. Shows the Help Center answers for whatever is active (the current ribbon
 * tab, a contextual tab such as Picture Format, or the open File-tab section) in a popover, with a link to the full
 * article in the Help Center. F1 opens it, as in Publisher. `variant` 'icon' (ribbon quick-access) or 'rail' (a row in the
 * File-tab side rail, which covers the ribbon); the popover sits above the Backstage (z-80 > z-70).
 */
export const HelpButton = ({ className = '', variant = 'icon', side = 'bottom', align = 'end', hotkey = true, testId }) => {
  const tid = testId || (variant === 'rail' ? 'backstage-help-btn' : 'ribbon-help-btn');
  const { state } = useEditor();
  const [open, setOpen] = useState(false);
  const contextKey = state.fileOpen ? `backstage:${state.backstageSection || 'home'}` : `ribbon:${state.ribbonTab || 'home'}`;
  const help = useMemo(() => helpFor(contextKey), [contextKey]);
  const [expanded, setExpanded] = useState([]);
  useEffect(() => { if (open) setExpanded(help.items[0] ? [help.items[0].key] : []); }, [open, help]);

  // F1 toggles whichever instance is on screen: the rail button while the File tab covers the ribbon, else the ribbon's "?"
  const owns = hotkey && (variant === 'rail' ? !!state.fileOpen : !state.fileOpen);
  const ownsRef = useRef(owns);
  ownsRef.current = owns;
  useEffect(() => { if (!owns) setOpen(false); }, [owns]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F1' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && ownsRef.current) { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const first = help.items[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === 'rail' ? (
          <button type="button" className={className} title={`Help for ${help.label} (F1)`} data-testid={tid} data-help-context={contextKey}>
            <CircleHelp className="h-4 w-4" /> Help
            <span className="ml-auto hidden md:inline text-[11px] text-muted-foreground truncate max-w-[110px]" data-testid="backstage-help-hint">{help.label}</span>
          </button>
        ) : (
          <button type="button" className={className} title={`Help for the ${help.label} (F1)`} aria-label={`Help for the ${help.label}`} data-testid={tid} data-help-context={contextKey}>
            <CircleHelp className="h-4 w-4" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent side={side} align={align} sideOffset={8} className="z-[80] w-[380px] max-w-[calc(100vw-16px)] p-0 bg-card" data-testid={`${tid}-popover`}>
        <div className="px-4 pt-3.5 pb-2 border-b border-border">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">Help</div>
          <div className="text-sm font-semibold leading-tight" style={heading} data-testid="ribbon-help-context">{help.label}</div>
        </div>
        <Accordion type="multiple" value={expanded} onValueChange={setExpanded} className="px-4 max-h-[min(60vh,420px)] overflow-y-auto" data-testid="ribbon-help-list">
          {help.items.map((it) => (
            <AccordionItem key={it.key} value={it.key} className="border-border" data-testid={`ribbon-help-item-${it.key}`}>
              <AccordionTrigger className="text-left text-[13px] font-medium hover:no-underline py-2.5">{it.q}</AccordionTrigger>
              <AccordionContent className="wp-legal wp-help wp-help-compact pb-3">
                {it.a}
                <a href={`/help#${it.key}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[12px] text-primary underline underline-offset-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`ribbon-help-open-${it.key}`}>
                  Open in Help Center<ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="px-4 py-2.5 border-t border-border flex items-center justify-between gap-2 text-[12px]">
          <span className="flex items-center gap-3">
            <a href={first ? `/help#${first.topic.id}` : '/help'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="ribbon-help-all">
              <Search className="h-3.5 w-3.5" aria-hidden="true" />Search all help
            </a>
            <a href="/about" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="ribbon-help-about">
              <Info className="h-3.5 w-3.5" aria-hidden="true" />About
            </a>
          </span>
          <span className="text-muted-foreground"><kbd className="rounded border border-border bg-muted px-1 py-px text-[10px] font-mono">F1</kbd> opens this</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
